/**
 * 제출 후 흐름 레지스트리 — 결정 2026-09-06 §1·§5 (TBM 제출 후 배포 흐름).
 *
 * 소유 분할(§1): **흐름 화면·설정 완료 판정(`isSetupComplete`)·진입 href = 이 레지스트리(web)** /
 * **흐름 키·표시 문구(name·ctaLabel) = shared `POST_SUBMIT_FLOWS` 데이터 정본**(저작 select와
 * 미리보기 CTA가 같은 값을 읽어야 하므로). 셸·공용 컴포넌트에는 도메인 어휘가 0이어야 하고,
 * 소비처(new-composed·ComposerReportDetail)는 이 레지스트리를 **인용만** 한다 — 「배포」 같은
 * 이름을 공용 문구·공용 분기에 박으면 다른 흐름이 등록되는 순간 거짓이 된다.
 *
 * 선언 판정은 **스냅샷 축**이다(`templateSnapshot.policy.postSubmit`) — 서버의 위임 완료 승격
 * skip 판정(§6 ⑤, W3b)과 같은 축이라, 위임/생성 후 라이브 템플릿의 정책이 바뀌어도 형제끼리
 * 흐름이 갈라지지 않는다. 스냅샷이 없는 옛 문서는 선언 없음으로 읽는다(회귀 안전 — 미선언은
 * 종전대로 제출에서 끝).
 *
 * **활동 진행 슬롯**(결정 2026-09-08 §C3): 흐름이 소유한 활동 축(TBM 교육 = `EducationSession`)의
 * 진행을 상세 chrome이 그리되, *그릴 것인가*는 **양식의 선언**이 정한다. 종전에는 상세가 선언을
 * 묻지 않고 교육 파생을 직접 불렀고, 다른 COMPOSED 양식에서 아무것도 안 뜬 이유는 「그 양식이
 * 교육 흐름이 아니라서」가 아니라 세션 행이 **우연히 비어서**였다 — 은닉된 결합이다. 슬롯은
 * 도메인 어휘가 없는 중립 형상(`ActivityProgress`)으로 주고받는다.
 */
import {
  findPostSubmitFlow,
  isTbmEducationPeriodEnded,
  TBM_DISTRIBUTION_FLOW_KEY,
  type ReportTemplate,
} from '@safetyhub/shared';
import type { Report } from '../../lib/reports';
import {
  resolveTbmEducationProgress,
  tbmEducationProgressLabel,
  tbmEducationRoundLabel,
} from '../../lib/tbmEducationProgress';
import { resendTbmEducatorLink } from '../../lib/tbmEducatorResend';
import {
  composedEditHref,
  isSiblingAwaitingWrite,
  resolveSiblingWriteHref,
} from './siblingWriteHref';

/** 활동 진행 세부 줄 하나 — 문구는 도메인이 완성해서 준다(셸은 그대로 그린다). */
/**
 * 활동 진행 상태 축 — 배지 색과 세부 줄이 읽는다. `preparing`(F-55)은 활동 행은 있는데 아직
 * 시작 전인 상태(TBM은 담당자가 입력만 해 회차가 lazy 생성된 경우) — 끝난 것이 아니므로
 * 색은 진행 중과 같은 amber다(teal은 「끝났다」에만 쓴다).
 *
 * `ended`(2026-09-30): 그 활동이 **더 진행되지 않는다** — 그러나 완료도 아니다(TBM은 교육 기간이
 * 끝났는데 못 다 한 경우). 어휘는 여전히 중립이다: 셸은 「교육」·「기간」을 모르고 *이어지지
 * 않는다*만 안다.
 *
 * ⚠️ **`completed`와 같은 색을 주지 않는다.** 둘 다 「끝났다」지만 teal은 *다 했다*는 뜻이고
 * (`ended`는 못 다 했다), amber는 *진행 중*이라 이 상태가 고치려던 거짓을 색으로 다시 말한다.
 * 그래서 중립 회색(slate)이다 — 색 축의 판정은 셸이 쥔다(`ComposerReportDetail` 배지).
 */
export type ActivityProgressState = 'preparing' | 'in-progress' | 'completed' | 'ended';

export interface ActivityProgressLine {
  /** React key — 도메인이 정한다(TBM은 회차 번호). */
  key: string;
  label: string;
  state: ActivityProgressState;
}

/**
 * 흐름이 소유한 **활동 축**의 진행 — 도메인 어휘가 없는 중립 형상이다. 셸(상세 chrome)이
 * 「교육」·「회차」·「일」을 몰라야 한다는 이 파일 헤더의 소유 규약 그대로다.
 */
export interface ActivityProgress {
  /** 배지 문구 한 줄. 도메인이 완성해서 준다. */
  badgeLabel: string;
  /** 배지 색 축 — 준비 중·진행 중 amber / 완료 teal(위임 진행 배지 규약과 같다). */
  state: ActivityProgressState;
  /** 세부 줄. **빈 배열이면 셸이 그리지 않는다** — 「그릴 것인가」 판정은 도메인 몫이다. */
  lines: ActivityProgressLine[];
}

/**
 * 흐름 판정(선언·설정 완료)의 **입력 재료 목록** — 이 레지스트리 파일이 소유한다.
 *
 * `Report` 전체를 요구하지 않는 이유: 같은 판정을 **부분 형상만 가진 surface**도 써야 한다.
 * 라벨 파생(`lib/reportStatusLabel`)이 그렇다 — 그쪽 입력(`ReportStatusLabelSource`)은
 * `status: string`으로 느슨하고, 실제로 `{ status }` 하나만 넘기는 자리가 있다
 * (`[id]/tbm-distribution-setup.tsx`). `Report`를 요구하면 그 자리가 막히고, 그러면 라벨이
 * 같은 축을 자기 파일에 다시 적게 된다(두 곳이 갈린다).
 *
 * 재료 목록이 **여기** 있는 것이 요점이다. 라벨 모듈은 이 타입을 확장하기만 하므로 그 소스에는
 * TBM 어휘가 한 번도 나오지 않고, 새 흐름이 새 재료를 읽으면 tsc가 「그 필드 없다」로 막아
 * 이 목록에 한 줄 추가를 **강제**한다 — 규약이 아니라 검사다.
 *
 * 상태 축(`status`/`type`)은 일부러 없다. 그건 호출부가 이미 가른 뒤에 이 판정을 부른다.
 */
export interface PostSubmitFlowSource {
  /** 선언 판정의 축 — 위 헤더의 "스냅샷 축" 그 자체. */
  templateSnapshot?: ReportTemplate | null;
  /**
   * 스냅샷을 실을 수 **없는** 응답이 대신 싣는 흐름 키(서버가 스냅샷에서 파생한다).
   *
   * 목록(`GET /reports`)은 `templateSnapshot`을 응답에서 **의도적으로 벗긴다** — dev 평균
   * 93KB·최대 177KB라 목록에 실을 무게가 아니다. 그런데 목록 배지(`DocStatusBadge`)도
   * 상세와 **같은 라벨 함수**를 쓰므로, 축이 없으면 교육이 도는 문서가 목록에서만 「초안」으로
   * 남는다(2026-09-08 dev 실측에서 적발 — 상세는 「진행 중」인데 목록은 「초안」이었다).
   *
   * ⚠️ 스냅샷을 **축소해서** 싣지 않는 이유: 일부 필드만 담은 `templateSnapshot`은 다른
   * 소비처가 `composerWidgetTree` 같은 것을 읽고 조용히 `undefined`를 받게 만든다. 없는 것은
   * 없다고 하고, 필요한 사실만 별도 스칼라로 준다.
   */
  postSubmitFlowKey?: string | null;
  /** `tbm-distribution`의 설정 완료 축. 다른 흐름은 자기 재료를 여기 더한다. */
  tbmSignatureMode?: Report['tbmSignatureMode'];
}

/** 흐름이 발송한 링크의 재발송 채널 — 도메인 중립 어휘(SMS/이메일). */
export type PostSubmitResendChannel = 'sms' | 'email';

/**
 * 흐름이 발송한 링크를 **같은 받는 사람에게 다시 보내는** 입구(F-27, 2026-09-11) — 상세 chrome
 * `postSubmitResend`의 재료. 셸(훅·상세·모달)은 「교육」·「담당자」를 몰라야 하므로 문구·받는 사람·
 * 실제 발송까지 흐름이 준다. 받는 사람은 고정이다(사용자 결정 「같은 담당자에게 재전송만」 —
 * 바꾸는 것은 설정 화면의 몫).
 */
export interface PostSubmitResendEntry {
  /** 버튼·모달 제목 문구. 도메인이 완성해서 준다. */
  label: string;
  /** 받는 사람 — 모달이 이름과 채널 가용성(전화·이메일 유무)을 이걸로 그린다. */
  recipient: { name: string; phone?: string | null; email?: string | null };
  /** 실제 재발송 — 흐름 소유 API. 성공 시 실제로 보낸 사람 이름을 돌려준다(토스트 인용). */
  send: (companyId: string, channels: PostSubmitResendChannel[]) => Promise<{ contactName: string }>;
}

export interface PostSubmitFlowEntry {
  /** `policy.postSubmit.flow` 키 — shared `POST_SUBMIT_FLOWS`에 같은 키가 반드시 있어야 한다. */
  key: string;
  /**
   * "이어질 흐름의 설정이 끝났는가" — §5: 이 판정이 상세 접근 라우팅(설정 미완이면 흐름으로
   * 안내)과 액션 노출의 축이다. 판정 재료는 문서 데이터뿐이다(templateKey 분기 금지).
   */
  isSetupComplete: (report: PostSubmitFlowSource) => boolean;
  /** 흐름 화면 진입 href — 마지막 저장 직행(§3)과 접근 라우팅(§5)이 같은 값을 쓴다. */
  setupHref: (reportId: string) => string;
  /**
   * 전환 예고 접미 문구(`작성 완료 → {ctaLabel}`) — shared `POST_SUBMIT_FLOWS` 인용.
   * 빈 문자열이면 데이터 정본에 키가 없는 것이다(등록 규약 위반 — spec이 대조한다).
   */
  ctaLabel: string;
  /**
   * 이 흐름이 소유한 **활동 축**의 진행 — 상세 chrome이 배지·세부 줄로 그린다.
   * 미선언(슬롯 없음)이거나 `null`이면 그리지 않는다.
   */
  activityProgress?: (report: Report) => ActivityProgress | null;
  /**
   * 그 **활동 화면**으로 가는 입구 — 위 배지가 가리키는 곳(상세 chrome `postSubmitActivity`).
   *
   * 목적지도 문구도 흐름이 소유한다. 셸(훅·상세)은 「언제 뜨는가」만 정하고, 어디로 가는지·뭐라
   * 적는지는 이 값에서 받는다 — `setupHref`+`ctaLabel` 짝과 같은 소유 분할이되, 활동 문구는
   * **진행 상태에 따라 바뀌므로**(구 TBM 상세의 「교육 화면」/「교육 기록」 쌍) 함수 하나로 준다.
   *
   * 미선언(활동 화면이 없는 흐름)이거나 `null`이면 그 버튼을 그리지 않는다.
   */
  activityEntry?: (report: Report) => { href: string; label: string } | null;
  /**
   * 이 흐름이 발송한 링크를 **같은 받는 사람에게 다시 보내는** 입구(상세 chrome `postSubmitResend`).
   *
   * 「이 문서에 다시 보낼 링크가 있는가」의 판정을 흐름이 소유한다 — 셸은 그 답이 `null`이
   * 아닌지만 본다(설정 완료·상태·배정 같은 셸 축은 훅이 따로 곱한다). 미선언(발송 링크가 없는
   * 흐름)이거나 `null`이면 그 버튼을 그리지 않는다.
   */
  resendEntry?: (report: Report) => PostSubmitResendEntry | null;
  /**
   * 이 흐름의 활동을 **누가 진행하는가** 한 줄(상세 chrome이 활동 배지 옆에 적는다, F-30).
   *
   * 「담당자 배포 / 직접 진행 / 오프라인」은 설정 화면에 들어가야 보였고 확정 뒤엔 그 화면이
   * 잠긴다 — 상세만 보는 사람은 누가 교육하는지 알 길이 없었다(2026-09-11 사용자 지적). 문구도
   * 판정도 흐름이 소유하고 셸은 문자열만 받는다. 설정 전(`null`)이면 그리지 않는다.
   */
  conductorNotice?: (report: Report) => string | null;
}

/**
 * 흐름 키 → entry. 흐름을 늘리려면 화면·판정을 구현해 여기 등록하고, shared
 * `POST_SUBMIT_FLOWS`에 표시 데이터 한 줄을 더한다(shared 쪽 doc 참조 — 손으로 적는 목록).
 */
export const POST_SUBMIT_FLOW_REGISTRY: Record<string, PostSubmitFlowEntry> = {
  'tbm-distribution': {
    key: 'tbm-distribution',
    // §5 확정: 배포 설정의 모든 갈래(담당자 배포·직접 진행·OFFLINE)가 서명 방식 확정을
    // 지나고, 구의 "서명 방식 불변" 규약과 맞물린다. "토큰 0 = 미배포" 술어는 직접 진행·
    // OFFLINE에서 영구 오판이라 기각됐다.
    isSetupComplete: (report) => report.tbmSignatureMode != null,
    // 서버 게이트/실행 엔드포인트(GET·POST /reports/:id/tbm-distribution-setup)와 동명 —
    // 화면 자체는 W4b가 채운다(그 전까지 플레이스홀더 라우트).
    setupHref: (reportId) => `/reports/${reportId}/tbm-distribution-setup`,
    ctaLabel: findPostSubmitFlow('tbm-distribution')?.ctaLabel ?? '',
    /**
     * 이 흐름의 활동 축 = **TBM 교육**. 판정도 문구도 `lib/tbmEducationProgress` → shared
     * 한 벌이 계속 소유하고, 여기서는 중립 형상으로 **옮기기만** 한다(같은 축을 두 번 적으면
     * 두 곳이 갈린다 — `feedback-same-concept-judged-by-different-axes-in-ui-and-server`).
     *
     * 세부 줄은 **다회차에서만**(`totalDays !== null`) 채운다. 단일회차에서 「1회차」는 셀 것이
     * 없는 셈이라 줄만 늘린다. 여기서 `isMultiSessionEducation`을 다시 부르지 않는 이유는 그
     * 축이 이미 `totalDays`에 접혀 있어서다 — 두 번 물으면 두 곳이 갈린다.
     */
    activityProgress: (report) => {
      const progress = resolveTbmEducationProgress(report);
      if (!progress) return null;
      return {
        badgeLabel: tbmEducationProgressLabel(progress),
        state: progress.state,
        lines:
          progress.totalDays === null
            ? []
            : progress.rounds.map((round) => ({
                key: String(round.round),
                label: tbmEducationRoundLabel(round),
                state: round.status,
              })),
      };
    },
    /**
     * 이 흐름의 활동 화면 = **TBM 교육 화면**(`/educations/tbm/:id`). 구 TBM 상세가 갖고 있던
     * 그 입구(`pages/tbm/[id].tsx`)와 **같은 목적지**다 — 화면은 이미 COMPOSED를 지원하는데
     * (결정 2026-09-08 `composed-education-runtime`) COMPOSED 상세에만 문이 없었다.
     *
     * 문구는 **진행 상태에서 파생**한다. 축은 위 `activityProgress`가 이미 쓰는
     * `resolveTbmEducationProgress` 하나다 — 「끝났나」를 여기서 다시 세면 배지와 버튼이 서로
     * 다른 말을 한다(배지는 「교육 완료」인데 버튼은 「교육 화면」인 식).
     *
     * ⚠️ 구 상세의 같은 자리는 `isEducationDone`이라는 **다른(더 엄격한) 술어**를 쓴다. 그것을
     * 옮겨 오지 않은 이유는 이 상세에 이미 진행 축이 하나 있어서다 — 축을 둘로 두면 같은 화면의
     * 배지와 버튼이 갈린다. 진행을 아직 모를 때(세션 0건 → `null`)는 시작 전이므로 「교육 화면」.
     */
    activityEntry: (report) => {
      const progress = resolveTbmEducationProgress(report);
      return {
        href: `/educations/tbm/${report.id}`,
        /**
         * 「기록」이 되는 갈래가 **둘**이다 — 전부 마쳤거나(`completed`), **기간이 끝났거나**
         * (2026-09-30). 뒤쪽은 `progress.state`로 답할 수 없다: 세션이 0건이면 `progress`가
         * `null`이라 상태 자체가 없기 때문이다(그 계약은 그대로 둔다 — 배지를 안 띄우는 것이
         * 옳다). 그래서 만료 술어를 **직접** 부른다.
         *
         * 축이 갈리는 것은 아니다 — 배지의 `'ended'`도 같은 `isTbmEducationPeriodEnded`가
         * 정하므로, 배지가 「교육 기간 종료」인 문서의 버튼은 반드시 「교육 기록」이다.
         *
         * 이것이 **표시뿐 아니라 사실**인 근거: 기간이 끝나면 그 화면에서 할 수 있는 것이
         * 읽기뿐이다(`assertEducationPeriodOpen`이 새 회차를 거부한다). 「교육 화면」은 지금
         * 무언가 할 수 있다는 뜻이라 거짓 안내가 된다.
         */
        label:
          progress?.state === 'completed' || isTbmEducationPeriodEnded(report)
            ? '교육 기록'
            : '교육 화면',
      };
    },
    /**
     * 이 흐름의 재발송 = **교육 담당자에게 교육 진행 링크(direct 토큰) 재전송**(F-27). 구 TBM
     * 상세의 「재전송」 모달(`pages/tbm/[id].tsx`, direct 토큰 필터)이 대응물이다 — COMPOSED
     * 상세엔 그 입구가 없어 리마인더 크론 말고는 다시 보낼 수단이 없었다.
     *
     * 세 축 전부가 있어야 링크가 있다:
     *  - `ELECTRONIC`: OFFLINE(출력물 서명)은 담당자 링크 자체가 없다.
     *  - 직접 진행 아님(`tbmSelfConducted !== true`): 작성자가 직접 진행하면 담당자도 토큰도
     *    없다(결정 2026-09-08). `undefined`(옛 응답)는 모른다 — 그때는 아래 스냅샷이 답한다.
     *  - 발송 스냅샷(`tbmEducatorDispatch`): 서버가 최신 활성 direct 토큰에서 내려준 받는 사람.
     *    이것이 없으면 다시 보낼 것이 없다(미확정·취소본·목록 형상).
     * 담당자 축을 `tbmEducatorContactId`로 잡지 않는 이유는 그 필드 JSDoc 참조(이름·연락처만 적은
     * 담당자는 컬럼이 null이다).
     *
     * 설정 완료(`isSetupComplete`)는 여기서 다시 묻지 않는다 — 훅이 형제 둘과 같은 자리에서 곱한다.
     * 두 번 물으면 두 곳이 갈린다.
     */
    resendEntry: (report) => {
      if (report.tbmSignatureMode !== 'ELECTRONIC') return null;
      if (report.tbmSelfConducted === true) return null;
      const dispatch = report.tbmEducatorDispatch;
      if (!dispatch) return null;
      return {
        label: '교육 링크 재전송',
        recipient: {
          name: dispatch.contactName,
          phone: dispatch.contactPhone,
          email: dispatch.contactEmail ?? null,
        },
        send: (companyId, channels) => resendTbmEducatorLink(report.id, companyId, channels),
      };
    },
    /**
     * 누가 교육하는가(F-30) — 배포 설정의 세 갈래를 그대로 말한다. 담당자 이름은 `resendEntry`와
     * 같은 축(`tbmEducatorDispatch` = 최신 활성 진행 토큰의 스냅샷)에서 읽는다 — 컬럼
     * `tbmEducatorContactId`는 이름·연락처만 적은 담당자에서 null이라 축으로 못 쓴다(그 JSDoc).
     * 위임 작성 담당자와는 **다른 역할**이라 라벨을 「교육 담당자」로 못박는다(dev에선 같은
     * 사람이라 착시가 있다).
     *
     * 담당자 배포인데 스냅샷이 없으면(목록 형상·취소본) 모르는 것이므로 `null` — 지어내지 않는다.
     *
     * 「발송됨 / 열람함」(F-53, 2026-09-13)은 스냅샷의 `firstAccessedAt`으로 가른다 — 위임 담당자의
     * 「발송됨→열람」과 같은 축이다. 서버는 첫 접속에 `REPORT_UPDATED`를 쏘므로 상세가 조용히 다시
     * 읽어 문구가 실시간으로 바뀐다. `undefined`(옛 응답)는 모른다이므로 「발송됨」으로 둔다.
     */
    conductorNotice: (report) => {
      if (report.tbmSignatureMode == null) return null;
      if (report.tbmSignatureMode === 'OFFLINE') return '오프라인 · 출력물 서명';
      if (report.tbmSelfConducted === true) return '직접 진행(안전관리자)';
      const dispatch = report.tbmEducatorDispatch;
      if (!dispatch) return null;
      const stage = dispatch.firstAccessedAt ? '열람함' : '발송됨';
      return `교육 담당자 ${dispatch.contactName} · 진행 링크 ${stage}`;
    },
  },
};

/**
 * 템플릿(또는 스냅샷)의 선언에서 entry 해소. 미선언·미등록 키는 undefined —
 * 호출부가 "이어질 흐름 없음(제출에서 끝)"으로 다룬다(shared `findPostSubmitFlow` 동형).
 */
export function templatePostSubmitFlow(
  template: ReportTemplate | null | undefined,
): PostSubmitFlowEntry | undefined {
  const flow = template?.policy?.postSubmit?.flow;
  return flow ? POST_SUBMIT_FLOW_REGISTRY[flow] : undefined;
}

/** 문서의 **스냅샷 선언**에서 entry 해소 — 위 doc의 "스냅샷 축" 그 자체. */
export function reportPostSubmitFlow(
  report: PostSubmitFlowSource,
): PostSubmitFlowEntry | undefined {
  // 스냅샷이 **먼저**다 — 상세는 스냅샷을 싣고, 그게 이 파일 헤더가 말하는 정본 축이다.
  // 스칼라는 스냅샷을 실을 수 없는 응답(목록)의 폴백이고, 서버가 같은 스냅샷에서 파생하므로
  // 둘이 갈릴 수 없다. 둘 다 없으면 선언 없음(옛 문서·미선언 양식 — 회귀 안전).
  const fromSnapshot = templatePostSubmitFlow(report.templateSnapshot);
  if (fromSnapshot) return fromSnapshot;
  const key = report.postSubmitFlowKey;
  return key ? POST_SUBMIT_FLOW_REGISTRY[key] : undefined;
}

/**
 * `setupHref` 패턴에서 reportId 자리를 찾기 위한 표식 — 경로 문자열을 손으로 적지 않으려고
 * 레지스트리에 **되물어** 패턴을 얻는다(경로는 흐름 소유라 여기 복제하면 두 곳이 갈린다).
 */
const SETUP_HREF_PROBE_ID = '__REPORT_ID__';

/**
 * **이 경로가 어느 흐름의 설정 화면인가** — 「확정하고 나면 되돌아가면 안 되는 곳」 판정(F-52).
 *
 * 설정을 확정한 뒤 그 화면으로 되돌아가면 「이미 확정됨」만 말하는 막다른 길이 된다. 그래서
 * 상세는 원점을 물려줄 때 이 술어로 걸러 낸다.
 *
 * ⚠️ **`entry.setupHref(report.id)`와의 일치 비교로는 부족하다** — 설정 화면의 「작성 이어가기」
 * 링크는 *다른 형제*의 상세(`/reports/rep-b`)로 보내면서 `from`에 *앵커의* 설정 경로
 * (`/reports/rep-a/tbm-distribution-setup`)를 싣는다. 그래서 id가 갈리고, 정작 막다른 길이 되는
 * 주경로가 그 비교를 빠져나간다. 여기서는 **id 자리를 비운 패턴**으로 대조해 흐름의 설정
 * 화면이면 누구의 것이든 잡는다.
 */
export function isPostSubmitSetupPath(path: string | null | undefined): boolean {
  if (!path) return false;
  // `from`은 쿼리를 달고 올 수 있다(`/reports?page=3`). 경로만 비교한다.
  const pathname = path.split(/[?#]/)[0];
  return Object.values(POST_SUBMIT_FLOW_REGISTRY).some((entry) => {
    const [head, tail] = entry.setupHref(SETUP_HREF_PROBE_ID).split(SETUP_HREF_PROBE_ID);
    // 표식이 안 박히는 형태(고정 경로)면 통째로 비교한다.
    if (tail === undefined) return head === pathname;
    if (!pathname.startsWith(head) || !pathname.endsWith(tail)) return false;
    const id = pathname.slice(head.length, pathname.length - tail.length);
    // id 자리는 **한 세그먼트**여야 한다 — 빈 값이나 `/`가 섞이면 다른 라우트다.
    return id.length > 0 && !id.includes('/');
  });
}

/**
 * **TBM 교육 축을 가진 문서인가** — 「이 문서가 TBM인가」가 아니라 **「TBM 교육 흐름을 도는가」**다.
 *
 * 순수 **선언 축**이다: `tbm-distribution` 흐름을 선언한 문서 — 유형 항과의 합집합이 아니다.
 * `ReportDeleteConfirmDialog`의 활동 축도 같은 선언에서 파생한다.
 *
 * ⚠️ **`type in ['TBM','COMPOSED']`로 넓히는 것은 틀린 처방이다**(결정 2026-09-08
 * `tbm-status-model-two-axes` [업데이트] — 사용자 지적으로 기각). 정체 축을 유지한 채 반지름만
 * 키우는 것이라, 교육과 무관한 Composer 문서(안전관리상태보고서 등)까지 딸려 들어온다.
 *
 * ⚠️ 그리고 「선언이 있는가」(유무)가 아니라 **흐름 키 동등**이다 — 위 dialog의 넓은 술어와
 * 일부러 다르다. 저쪽이 묻는 것은 "이어질 활동이 있나"라 흐름이 무엇이든 답이 같지만, 여기서
 * 묻는 것은 "**교육** 담당자·서명 수·교육 시간이 이 문서에 존재하는가"이고 그것을 채우는 것은
 * `tbm-distribution` 하나다(shared `isTbmDistributionFlowDocument`의 같은 논거). 좁은 쪽이
 * 안전한 기본값이다.
 *
 * 키를 리터럴로 적지 않고 `TBM_DISTRIBUTION_FLOW_KEY`를 인용한다 — 키를 개명하면 이 게이트가
 * 조용히 발동을 멈추므로 그 상수 하나에 묶어 둔다.
 *
 * 소비처는 문서 목록 행 표시(`pages/reports/index.tsx`)와 일정 일별 목록(`DayInspectionList`)이다.
 * (입력 타입에 `type` 축은 없다 — 넘길 값이 없다.)
 */
export function isTbmEducationDocument(report: PostSubmitFlowSource): boolean {
  return reportPostSubmitFlow(report)?.key === TBM_DISTRIBUTION_FLOW_KEY;
}

/**
 * 상세 진입 라우팅 파생(§5 표) — **postSubmit 선언 문서만** 대상이고, 클라 UX 라우팅이다
 * (보안 축 아님 — 서버 게이트는 별도). 미선언 문서는 무조건 상세(회귀 안전, S24 대조군).
 *
 * | 문서 상태 | kind | 목적지 |
 * |---|---|---|
 * | CREATING(직접·본인) | write | 작성 화면(형제 칩과 같은 술어·같은 href) |
 * | CREATING(위임) | delegation | 위임 현황 뷰 — **W4b 전까지 상세**(사후 조작 chrome이 이미 상세에 있다) |
 * | DRAFT + 설정 미완 + 묶음 준비됨 | setup | 흐름 화면 |
 * | DRAFT + 설정 미완 + **형제 칩 유래** | detail | 상세(아래 `PostSubmitEntryOrigin`) |
 * | DRAFT + 설정 미완 + **미완 형제 잔존/로딩 중** | detail | 상세(아래 `hasUnfinishedBundleSibling`) |
 * | 그 외(설정 완료·제출 이후·타인 CREATING) | detail | 상세(정위치) |
 *
 * ⚠️ **이 파생은 «진입 시점»에 한 번만 쓰인다** — 호출부(상세)가 첫 판정을 굳힌다. 형제 목록이
 * 실시간 갱신되므로 매 렌더 다시 쓰면, 읽고 있는 화면이 그 사이의 상태 변화로 통째로 바뀐다
 * (다음 단계가 있다는 사실·준비 여부는 `resolvePostSubmitNextStepNotice`가 **안내로** 말한다).
 *
 * 근거(§5): 구 마법사는 원자적(내용+배포 설정→생성)이라 "설정 미완 문서의 상세"라는 상태가
 * 없었다 — 이 중간 상태는 분할이 만든 것이므로 "아직 만드는 중"으로 취급한다.
 *
 * 판정 순서가 계약이다: **위임 → 작성 → 설정**. 위임본도 authorId는 위임자라, 위임 검사를
 * 작성 술어(`isSiblingAwaitingWrite` = CREATING+본인)보다 먼저 두지 않으면 위임자가 자기
 * 위임본의 작성 화면으로 끌려간다(서버 canEdit는 위임 중 편집을 막으므로 튕김 왕복이 된다).
 *
 * 타인 CREATING(작성 술어 불충족)은 상세로 둔다 — 서버 canEdit 축(DRAFT 동형: 작성자)과
 * 정렬해, 상세→작성→canEdit 거부→상세의 리다이렉트 루프를 구조적으로 막는다.
 */
export type PostSubmitEntryRoute = {
  kind: 'write' | 'delegation' | 'setup' | 'detail';
  href: string;
};

/**
 * **어떻게 들어왔는가** — 재료(`PostSubmitBundleContext`)와 성질이 달라 인자를 가른다.
 *
 * 설정 갈래에만 걸린다. 작성 갈래(CREATING)는 되돌릴 수 없는 값을 정하는 자리가 아니라
 * 「안 쓴 형제를 누르면 쓰러 간다」가 자연스럽고, 위임 갈래는 목적지가 같은 상세라 이동 자체가
 * 없다.
 */
export interface PostSubmitEntryOrigin {
  /**
   * 형제 칩에서 온 이동인가(`components/reports/siblingNavState`).
   *
   * 형제 칩을 누른 사람은 「옆 문서를 보겠다」고 누른 것이다. 그런데 설정 갈래는 `replace`
   * 이동이라 그 형제 상세로 **돌아갈 수도 없다** — 되돌릴 수 없는 값(서명 방식)을 정하는
   * 화면은 사용자가 스스로 들어가야 한다(진입로는 상세의 「다음 단계」 안내).
   */
  viaSiblingChip?: boolean;
  /**
   * 설정 화면의 「← 상세」에서 온 이동인가 (F-61). 형제 칩과 같은 사유 — 사용자가 상세를
   * 보겠다고 누른 것이라 설정 갈래로 되돌리지 않는다(되돌리면 왕복·깜빡임·진입 state 소실).
   */
  viaSetupBack?: boolean;
}

/**
 * 묶음 준비 상태 — 상세가 아는 형제 목록을 그대로 건넨다(`useSiblingReports`).
 * 없으면(생성 직후 호출부처럼 형제를 모를 때) 종전 동작이다.
 */
export interface PostSubmitBundleContext {
  /** 형제 목록(자기 자신 포함). */
  siblings: readonly Pick<Report, 'id' | 'status'>[];
  /**
   * 첫 로드가 아직 안 끝났는가 — 빈 배열이 "단건"인지 "아직 모름"인지 가른다(F-7과 같은 축).
   * 로딩 중에 판정하면 **미완 형제 없음으로 오판해 그대로 튕긴다.**
   */
  isLoading: boolean;
}

/**
 * 묶음에 **아직 작성이 안 끝난 형제**가 있는가 — 라우팅이 쓰는 한 축.
 *
 * ⚠️ 서버 게이트(`assessTbmDistributionSetupGate`)를 흉내 내지 않는다. 사유는 다섯인데 여기
 * 필요한 것은 하나뿐이고, 재파생하면 두 판정이 갈린다. 술어는 서버의 `NOT_ALL_DRAFT`와
 * **같은 식**이다(**미작성 = CREATING**, 2026-09-13 F-51) — 그것만 클라가 가진 목록으로 잴 수
 * 있고, 나머지(위임 진행·확정 여부)는 형제 목록 응답에 실리지 않는다.
 *
 * 종전 식은 「DRAFT·PENDING_APPROVAL이 아니면 미완」이었다 — 그러면 **APPROVED**(확정·결재까지
 * 끝난 형제)가 「아직 작성 중」으로 세어져, 형제가 결재 완료된 뒤 재생성·재위임으로 합류한 새
 * 형제의 진입 배너가 영영 비활성이었다(사용자 실측 2026-09-13: 「완료(비활성) 버튼만 떠」).
 * 확정 済 형제는 설정 화면이 행 단위로 잠글 뿐 차단 사유가 아니다(결정 2026-09-08 확장).
 *
 * 판정을 화면에 흩지 않으려고 이름 있는 순수 함수로 둔다 — 다음 사람이 자기 화면에서 다시
 * 세면 같은 사건에 화면이 다르게 반응한다.
 */
export function hasUnfinishedBundleSibling(
  siblings: readonly Pick<Report, 'id' | 'status'>[],
  reportId: string,
): boolean {
  return countUnfinishedBundleSiblings(siblings, reportId) > 0;
}

/**
 * 위 술어의 **개수판** — 안내가 「남은 문서 N건 작성 중」이라 말하려면 수가 필요하다.
 *
 * 술어를 여기 한 곳에 두고 `hasUnfinishedBundleSibling`이 이것을 쓴다 — 두 곳이 각자 세면
 * 「막혔다고 말하는 축」과 「몇 건이라고 말하는 축」이 갈려, 사유는 0건인데 버튼은 잠긴(또는 그
 * 반대) 화면이 된다.
 */
export function countUnfinishedBundleSiblings(
  siblings: readonly Pick<Report, 'id' | 'status'>[],
  reportId: string,
): number {
  // 서버 게이트 `NOT_ALL_DRAFT`(`d.status === 'CREATING'`)와 같은 식 — 여기를 바꾸면 거기도.
  return siblings.filter((s) => s.id !== reportId && s.status === 'CREATING').length;
}

/** 열려 있는 상세가 그리는 「다음 단계」 — 지금 갈 수 있으면 활성, 아니면 비활성+사유. */
export interface PostSubmitNextStepNotice {
  /** 지금 흐름 화면에 들어갈 수 있는가. false면 비활성 CTA + 아래 개수로 사유를 적는다. */
  ready: boolean;
  /** 흐름 화면 진입 href — 비활성일 때도 같은 목적지다(그래야 라벨이 거짓이 아니다). */
  href: string;
  /** 버튼 라벨 — 흐름 소유(`ctaLabel`). 사유는 라벨에 넣지 않는다. */
  ctaLabel: string;
  /** 사유의 재료 — 아직 작성이 안 끝난 형제 수. `ready`면 0이다. */
  unfinishedSiblingCount: number;
}

/**
 * 열려 있는 상세에 「다음 단계」를 어떻게 보여줄 것인가 (2026-09-10).
 *
 * 진입 라우팅은 **진입 시점에 한 번만** 판정한다(상세가 그렇게 쓴다) — 형제 목록이 실시간으로
 * 갱신되는데 파생이 바뀔 때마다 이동하면, 1번 그룹을 읽는 중에 담당자가 마지막 그룹을 끝내는
 * 순간 화면이 통째로 바뀐다. 흐름 화면은 되돌릴 수 없는 값(서명 방식)을 정하는 자리라 사용자가
 * **스스로 들어가야** 한다. 그래서 자동으로 옮기지 않는 대신 이 안내가 **그 뒤의 유일한
 * 진입로**가 된다.
 *
 * ⚠️ **두 상태를 한 함수가 답한다**(2026-09-10 사용자 확정 「나」안). 준비되기 전에도 안내를
 * 띄우되 **비활성 CTA + 사유**로 둔다 — 종전엔 미완인 동안 「배포 설정이라는 다음 단계가
 * 있다」는 사실 자체를 화면에서 알 수 없었다(리다이렉트는 억제됐고 재진입 액션은 안 뜬다).
 * 활성/비활성을 두 함수로 가르면 「같은 질문 두 곳」이 되어 버튼과 사유가 갈릴 수 있다.
 *
 * ⚠️ 상세의 「배포 설정」 액션(`useComposedReportActions`)은 이 자리를 채우지 못한다 — 그
 * 게이트는 `isSetupComplete`라 **설정을 마친 뒤 되돌아가는 재진입 전용**이고, 여기는 정확히
 * 그 반대(아직 미설정)다. 그 버튼이 뜨는 상태와 이 안내가 뜨는 상태는 **배타적**이라 진입로가
 * 둘로 늘지 않는다.
 *
 * ⚠️ 사유는 **서버 게이트의 재현이 아니다.** 판정 정본은 `assessTbmDistributionSetupGate`이고
 * (사유 다섯), 여기 있는 것은 CTA 옆에 적는 **한 줄**뿐이다 — 클라가 가진 형제 목록으로 셀 수
 * 있는 축 하나(미완 형제 수)만 말한다. 나머지 사유는 그 화면이 안내한다.
 *
 * 뜨는 조건은 활성·비활성이 **같다** — 선언 ∧ DRAFT ∧ 설정 미완 ∧ 작성자 본인(배포 설정은
 * 위임자의 동사). CREATING은 아직 작성 전이라 이 이야기를 꺼낼 자리가 아니고, 그건 DRAFT 조건이
 * 이미 막는다. 형제 목록 로딩 중에는 **아무것도 말하지 않는다** — 사유의 개수를 모르기 때문이다
 * (「0건 남았다」와 「모른다」는 다르다).
 */
export function resolvePostSubmitNextStepNotice(
  report: Report,
  userId: string | undefined,
  bundle: PostSubmitBundleContext,
): PostSubmitNextStepNotice | null {
  const entry = reportPostSubmitFlow(report);
  if (!entry) return null;
  if (report.status !== 'DRAFT' || entry.isSetupComplete(report)) return null;
  if (!userId || report.authorId !== userId) return null;
  if (bundle.isLoading) return null;
  const unfinishedSiblingCount = countUnfinishedBundleSiblings(bundle.siblings, report.id);
  return {
    ready: unfinishedSiblingCount === 0,
    href: entry.setupHref(report.id),
    ctaLabel: entry.ctaLabel,
    unfinishedSiblingCount,
  };
}

/**
 * 검수 순회 맥락인가(S33/F-3) — **앵커(현재 편집 중인 문서)** 로 판정한다.
 *
 * 검수 = 배포 설정의 「내용 확인·수정」으로 들어온 편집 세션: 앵커가 **DRAFT**(작성 완료본)
 * ∧ 스냅샷 postSubmit 선언 ∧ 흐름 설정 미완. 설정 완료 여부를 앵커로 판정해도 묶음 판정이
 * 되는 근거는 §4 "설정+발송 = 한 액션"(묶음 단위 1회, 부분 확정은 전체 롤백)이다 — 형제
 * 목록 응답에는 설정 축(tbmSignatureMode)이 실리지 않아 형제별 판정은 애초에 불가능하다.
 *
 * 앵커가 CREATING(이어 작성 세션)이면 검수 맥락이 아니다 — 그때 완료(DRAFT) 형제 칩은
 * 종전대로 상세(→ §5 라우팅이 배포 설정으로 안내)가 정위치다(S6 "완료된 건을 누르면 하다 만
 * 배포 설정으로").
 *
 * ⚠️ 그 안내가 서는 곳은 **작성 화면 호스트뿐이다**(2026-09-10). 상세의 형제 칩은 진입 축
 * (`PostSubmitEntryOrigin.viaSiblingChip`)을 실어 보내 §5 라우팅이 설정 갈래에서 상세에
 * 머물게 한다 — 거기서 옆 칩을 누르는 것은 「옆 문서를 읽겠다」라 행위가 다르다. 그래서 축을
 * 싣는 쪽이 칩이 아니라 호스트다(`SiblingReportChips.navState`).
 */
export function isPostSubmitReviewTraversal(anchor: Report): boolean {
  if (anchor.status !== 'DRAFT') return false;
  const entry = reportPostSubmitFlow(anchor);
  return !!entry && !entry.isSetupComplete(anchor);
}

/**
 * 작성 화면(new-composed) 형제 칩의 목적지 — 작성 축(`resolveSiblingWriteHref` = CREATING+본인)
 * 위에 **검수 순회 축**(S33/F-3)을 얹는다: 검수 맥락이면 DRAFT+작성자 본인 형제를 편집 URL로
 * 연다(칩 순회 검수 — 문 하나, 탭 체감). 클라이언트가 아는 축만 쓰며, 오판 안전망은 기존
 * 그대로다(new-composed 진입 시 `canEditReport` 불가면 상세로 replace). 상세 화면의 형제
 * 칩은 이 함수를 쓰지 않는다(항상 상세).
 */
export function resolveWriteScreenSiblingHref(
  sibling: Report,
  userId: string | undefined,
  reviewTraversal: boolean,
): string {
  if (
    reviewTraversal &&
    sibling.status === 'DRAFT' &&
    Boolean(userId) &&
    sibling.authorId === userId
  ) {
    return composedEditHref(sibling.templateKey, sibling.id);
  }
  return resolveSiblingWriteHref(sibling, userId);
}

export function resolvePostSubmitEntryRoute(
  report: Report,
  userId: string | undefined,
  bundle?: PostSubmitBundleContext,
  origin?: PostSubmitEntryOrigin,
): PostSubmitEntryRoute {
  const detail: PostSubmitEntryRoute = { kind: 'detail', href: `/reports/${report.id}` };
  const entry = reportPostSubmitFlow(report);
  if (!entry) return detail;

  if (report.status === 'CREATING') {
    if (report.delegatedContactId) {
      // W4b(위임 현황 뷰)가 이 href만 교체한다 — 갈래(kind)는 지금부터 존재한다.
      return { kind: 'delegation', href: `/reports/${report.id}` };
    }
    if (isSiblingAwaitingWrite(report, userId)) {
      return { kind: 'write', href: resolveSiblingWriteHref(report, userId) };
    }
    return detail;
  }
  if (report.status === 'DRAFT' && !entry.isSetupComplete(report)) {
    // 형제 칩에서 왔으면 **상세에 머문다**(2026-09-10) — 「옆 문서를 보겠다」는 이동이지 새
    // 진입이 아니다. 설정 갈래에만 거는 이유는 그 화면이 되돌릴 수 없는 값(서명 방식)을
    // 정하는 자리라서다(아래 굳힘 축과 같은 사유). 진입로는 상세의 안내 배너가 소유한다.
    if (origin?.viaSiblingChip) return detail;
    // 설정 화면의 「← 상세」에서 왔어도 **상세에 머문다**(F-61, 2026-09-14) — 같은 사유다.
    // 안 그러면 상세가 곧장 설정으로 `replace`돼 왕복하고, 그 왕복이 진입 state를 잃어
    // 「←」 라벨이 「보고서 목록」으로 바뀌었다(2차 회귀 R10 실측).
    if (origin?.viaSetupBack) return detail;
    // 묶음이 아직 준비되지 않았으면 **상세에 머문다**(2026-09-10 실측). 형제 하나가 완료되는
    // 순간 그 문서 상세가 배포 설정으로 `replace` 이동했는데, 서버 게이트가 NOT_ALL_DRAFT로
    // 막는 화면이라 열자마자 「아직 안 된다」이고 replace라 **그 문서 상세로 돌아갈 수도
    // 없었다.** 판정은 아래 한 축뿐이다 — 나머지 사유는 그 화면이 안내한다.
    if (bundle && (bundle.isLoading || hasUnfinishedBundleSibling(bundle.siblings, report.id))) {
      return detail;
    }
    return { kind: 'setup', href: entry.setupHref(report.id) };
  }
  return detail;
}
